/**
 * GNews news adapter — implements ProviderAdapter<NewsParams, RawGNews, NewsData>.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   CLIENT-SIDE:  paramsSchema, dataShape, cacheKey, glanceView
 *   SERVER-SIDE:  buildRequest, responseSchema, normalize
 *
 * `buildRequest` is THE ONLY place a URL exists in the whole system.
 * API keys are INJECTED as a parameter (never hardcoded, never logged).
 *
 * GNews API docs: https://gnews.io/docs/v4
 * Top-headlines endpoint: GET https://gnews.io/api/v4/top-headlines
 *
 * Identity:
 *   type     = 'news.headlines'
 *   provider = 'gnews'
 *   tier     = 'free'
 *   ttlSec   = 600 (10-minute free-tier TTL per registry)
 */

import { z } from 'zod';
import { newsParamsSchema, deriveCacheKey, type NewsParams } from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { GlanceViewModel, ProviderAdapter, UpstreamRequest } from 'briefpane/contracts';
import { rawGNewsSchema, normalize, type RawGNews, type NewsData } from './normalize.js';
import { glanceView } from './glance.js';

// ---------------------------------------------------------------------------
// dataShape Zod schema
// Keys must match DATA_SHAPE_KEYS['news.headlines']:
//   headline, source, publishedAt, itemCount
// ---------------------------------------------------------------------------

const newsDataShape = z.object({
  headline: z.string(),
  source: z.string(),
  publishedAt: z.string(),
  itemCount: z.number().int().min(0),
});

// ---------------------------------------------------------------------------
// The adapter object (implements ProviderAdapter<NewsParams, RawGNews, NewsData>)
// ---------------------------------------------------------------------------

/**
 * The news adapter with a build-request signature that accepts an injected API key.
 *
 * This is a slight extension of the base ProviderAdapter interface to allow:
 *   - Key injection without hardcoding (server always calls buildRequest(params, apiKey))
 */
export interface NewsAdapterShape
  extends Omit<ProviderAdapter<NewsParams, RawGNews, NewsData>, 'buildRequest'> {
  /** Server-side: build the GNews upstream request, injecting the API key. */
  buildRequest(params: NewsParams, apiKey: string): UpstreamRequest;
  /** Map normalized data + glance map to the H3 view-model. */
  glanceView(data: NewsData, glance: GlanceMap): GlanceViewModel;
}

/**
 * The GNews top-headlines adapter.
 *
 * Satisfies H2 (ProviderAdapter) with an extended buildRequest that accepts the
 * API key as a parameter (server-side injection). The registry / Worker injects
 * the key at call time from environment variables — it never appears in any config,
 * log, or client bundle (ADR-002 §5, §7 #1).
 *
 * GNews topic param maps to their `topic` query parameter which is a fixed enum
 * matching our NEWS_TOPICS (world/nation/business/technology/entertainment/
 * sports/science/health). The `count` param maps to `max` (GNews name for count).
 *
 * Ref: https://gnews.io/docs/v4#top-headlines
 */
export const newsAdapter: NewsAdapterShape = {
  // ---- Identity (both halves) ----
  type: 'news.headlines',
  provider: 'gnews',
  tier: 'free',
  ttlSec: 600,

  // ---- CLIENT-SIDE ----
  paramsSchema: newsParamsSchema,
  dataShape: newsDataShape,

  cacheKey: (params: NewsParams): string =>
    deriveCacheKey({ type: 'news.headlines', provider: 'gnews', params }),

  glanceView: (data: NewsData, glance: GlanceMap): GlanceViewModel =>
    glanceView(data, glance),

  // ---- SERVER-SIDE ONLY ----
  responseSchema: rawGNewsSchema,

  /**
   * Build the GNews top-headlines request.
   *
   * THE ONLY PLACE A URL EXISTS IN THE SYSTEM (ADR-002 §5, §7 #1).
   * Never call this client-side. The `apiKey` is injected from server env.
   * Never log the returned URL — it contains the key.
   *
   * GNews top-headlines: https://gnews.io/api/v4/top-headlines
   * Params:
   *   - topic: maps our NEWS_TOPICS to GNews `topic` param
   *   - max: the `count` from params (GNews uses `max` for article count)
   *   - apikey: the injected GNews API key
   *   - lang: 'en' (hardcoded for v1; internationalization is future scope)
   *
   * Ref: https://gnews.io/docs/v4#top-headlines
   */
  /**
   * Build the GNews request URL.
   *
   * Endpoint switch (D-017 §"Rung-1 news keyword query"):
   *   - query present → GNews /search endpoint; topic is the display label only.
   *     URL-encodes the query to handle spaces/special chars safely.
   *   - query absent  → GNews /top-headlines endpoint with topic param (existing path).
   *
   * THE ONLY PLACE A URL EXISTS (ADR-002 §2.1, §7 #1). API key injected, never logged.
   */
  buildRequest: (params: NewsParams, apiKey: string): UpstreamRequest => {
    if (params.query) {
      const q = encodeURIComponent(params.query);
      return {
        url: `https://gnews.io/api/v4/search?q=${q}&max=${params.count}&lang=en&apikey=${apiKey}`,
        method: 'GET',
      };
    }
    return {
      url: `https://gnews.io/api/v4/top-headlines?topic=${params.topic}&max=${params.count}&lang=en&apikey=${apiKey}`,
      method: 'GET',
    };
  },

  normalize: (raw: RawGNews): NewsData => normalize(raw),
};
